import * as React from 'react';
import PropTypes from 'prop-types';
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Skeleton from "@mui/material/Skeleton";
import Image from 'material-ui-image';
import {IconButton, ImageListItemBar, Typography, Box, Divider, Paper, Chip} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import {useState} from "react";
import CloseIcon from "@mui/icons-material/Close";


function VisualSearchDetail({item, handleClose}){

    const details = [
        { label: "Id", value: item?.id },
        { label: "Name", value: item?.name },
        { label: "Time Created", value: item?.timeCreated },
        { label: "Visual Class Id", value: item?.visualClassId },
    ];

    return (
        <Dialog
            open={!!item}
            onClose={() => handleClose()}
            maxWidth={'lg'}
            fullWidth={true}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '95vh',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                }
            }}
        >
            {
                !!item && (
                   <>
                       <DialogTitle 
                           sx={{
                               display: 'flex',
                               alignItems: 'center',
                               background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                               color: 'white',
                               pb: 2.5,
                               pt: 3,
                               px: 4,
                               position: 'relative',
                               '&::after': {
                                   content: '""',
                                   position: 'absolute',
                                   bottom: 0,
                                   left: 0,
                                   right: 0,
                                   height: '1px',
                                   background: 'rgba(255, 255, 255, 0.1)'
                               }
                           }}
                       >
                           <Typography 
                               variant="h5" 
                               component="span" 
                               sx={{ 
                                   fontWeight: 700, 
                                   flexGrow: 1,
                                   letterSpacing: '-0.02em',
                                   textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                               }}
                           >
                               {item.name ?? 'Unnamed Visual Search Item'}
                           </Typography>
                           <IconButton
                               onClick={() => handleClose()}
                               sx={{
                                   ml: 2,
                                   color: 'white',
                                   backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                   backdropFilter: 'blur(10px)',
                                   transition: 'all 0.2s ease',
                                   '&:hover': {
                                       backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                       transform: 'scale(1.05)'
                                   }
                               }}
                           >
                               <CloseIcon />
                           </IconButton>
                       </DialogTitle>
                       <DialogContent
                           sx={{
                               p: 0,
                               display: 'flex',
                               gap: 0,
                               flexDirection: { xs: 'column', md: 'row' },
                               backgroundColor: 'background.default',
                               overflow: 'hidden'
                           }}
                       >
                           <Box
                               sx={{
                                   flex: '2 1 0',
                                   minWidth: 0,
                                   p: 4,
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   backgroundColor: '#f8f9fa',
                                   backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)',
                                   backgroundSize: '20px 20px'
                               }}
                           >
                               <Paper
                                   elevation={0}
                                   sx={{
                                       width: '100%',
                                       maxWidth: '100%',
                                       borderRadius: 3,
                                       overflow: 'hidden',
                                       boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                                       backgroundColor: 'white',
                                       border: '1px solid',
                                       borderColor: 'divider',
                                       transition: 'all 0.3s ease',
                                       '&:hover': {
                                           boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                                           transform: 'translateY(-2px)'
                                       }
                                   }}
                               >
                                   <Box
                                       sx={{
                                           position: 'relative',
                                           width: '100%',
                                           paddingTop: '56.25%',
                                           backgroundColor: '#f5f5f5',
                                           overflow: 'hidden'
                                       }}
                                   >
                                       <img
                                           style={{
                                               position: 'absolute',
                                               top: 0,
                                               left: 0,
                                               width: '100%',
                                               height: '100%',
                                               objectFit: 'contain',
                                               display: 'block'
                                           }}
                                           src={item.mediaUrl}
                                           alt={item.name ?? 'unnamed Item'}
                                           loading="lazy"
                                       />
                                   </Box>
                               </Paper>
                           </Box>
                           <Box
                               sx={{
                                   flex: '1 1 0',
                                   minWidth: { xs: '100%', md: '380px' },
                                   maxWidth: { xs: '100%', md: '380px' },
                                   p: 4,
                                   backgroundColor: 'background.paper',
                                   borderLeft: { md: '1px solid' },
                                   borderTop: { xs: '1px solid', md: 'none' },
                                   borderColor: 'divider',
                                   display: 'flex',
                                   flexDirection: 'column'
                               }}
                           >
                               <Box sx={{ mb: 3 }}>
                                   <Typography 
                                       variant="h6" 
                                       sx={{ 
                                           fontWeight: 700, 
                                           mb: 1,
                                           color: 'text.primary',
                                           letterSpacing: '-0.01em'
                                       }}
                                   >
                                       Details
                                   </Typography>
                                   <Divider sx={{ mb: 0 }} />
                               </Box>
                               <Box 
                                   sx={{ 
                                       display: 'flex', 
                                       flexDirection: 'column', 
                                       gap: 3,
                                       flex: 1
                                   }}
                               >
                                   {details.map((detail, index) => (
                                       <Paper
                                           key={index}
                                           elevation={0}
                                           sx={{
                                               p: 2.5,
                                               borderRadius: 2,
                                               backgroundColor: index % 2 === 0 ? 'action.hover' : 'transparent',
                                               border: '1px solid',
                                               borderColor: 'divider',
                                               transition: 'all 0.2s ease',
                                               '&:hover': {
                                                   backgroundColor: 'action.selected',
                                                   borderColor: 'primary.main',
                                                   transform: 'translateX(4px)'
                                               }
                                           }}
                                       >
                                           <Typography 
                                               variant="caption" 
                                               sx={{ 
                                                   fontWeight: 700, 
                                                   mb: 1,
                                                   color: 'text.secondary',
                                                   textTransform: 'uppercase',
                                                   letterSpacing: 1,
                                                   fontSize: '0.7rem',
                                                   display: 'block'
                                               }}
                                           >
                                               {detail.label}
                                           </Typography>
                                           {detail.value ? (
                                               <Typography 
                                                   variant="body1" 
                                                   sx={{ 
                                                       color: 'text.primary',
                                                       wordBreak: 'break-word',
                                                       fontWeight: 500,
                                                       fontSize: '0.95rem',
                                                       lineHeight: 1.6
                                                   }}
                                               >
                                                   {detail.value}
                                               </Typography>
                                           ) : (
                                               <Chip 
                                                   label="N/A" 
                                                   size="small" 
                                                   sx={{ 
                                                       backgroundColor: 'action.disabledBackground',
                                                       color: 'text.disabled',
                                                       fontSize: '0.75rem'
                                                   }} 
                                               />
                                           )}
                                       </Paper>
                                   ))}
                               </Box>
                           </Box>
                       </DialogContent>
                   </>
                )
            }
        </Dialog>
    )
}

function LoadedState({items, onDeleteClick, onTagClick}) {
    const [details, setDetails] = useState(null);
    return (
        <>
            <VisualSearchDetail
                item={details}
                handleClose={() => {
                    setDetails(null)
                }}
            />
            <ImageList cols={5} gap={10}>

                {
                    items.map((item) => (
                            <ImageListItem
                                key={item.id}
                                variant="masonry"
                            >
                                <Image
                                    src={item.mediaUrl}
                                    alt={item.name}
                                    onClick={() => setDetails(item)}
                                    style={{
                                        cursor: 'pointer',
                                        "&:hover": {
                                            border: "1px solid #1DB8E0"
                                        },
                                    }}
                                />
                                <ImageListItemBar
                                    title={item.name}
                                    subtitle={item.timeCreated}
                                    actionIcon={
                                        <>
                                            <IconButton
                                                sx={{ color: 'error.main' }}
                                                onClick={_ => onDeleteClick(item)}
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                            <IconButton
                                                sx={{ color: 'primary.main' }}
                                                onClick={_ => onTagClick(item)}
                                            >
                                                <LocalOfferIcon/>
                                            </IconButton>
                                        </>
                                    }

                                />
                            </ImageListItem>
                        )
                    )
                }

            </ImageList>
        </>


    )
}

function LoadingState() {
    return (
        <ImageList cols={5} gap={10}>
            {[...Array(50).keys()].map((item) => (
                <ImageListItem
                    key={item}
                    style={{
                        backgroundColor: '#f2f2f2',
                        padding: '3px'
                    }}
                    sx={{
                        cursor: 'pointer'
                    }}
                >
                    <Skeleton height={200} variant={'rectangular'}/>
                </ImageListItem>
            ))}
        </ImageList>
    )
}




function SearchVisuaImageList(
    {
        loading,
        items,
        onDeleteClick,
        onTagClick,
    }
) {
    return (
        <>
            {
                loading
                    ? <LoadingState/>
                    : (
                        <LoadedState
                            items={items}
                            onDeleteClick={onDeleteClick}
                            onTagClick={onTagClick}
                        />
                    )
            }
        </>
    )
}

SearchVisuaImageList.propTypes = {
    loading: PropTypes.bool,
    items: PropTypes.array,
}

SearchVisuaImageList.defaultProps = {
    loading: false,
}

export default SearchVisuaImageList;
